import { useEffect, useState } from "react";
import ContadorMinimo from "../ContadorMinimo";
import { connect } from "react-redux";
import { Overlay, Card } from "./styled";
import Formulario from "../Formulario";
import closeIcon from "../../../assets/icons/close.svg";
import SolicitacaoProfissional, { TIPOS_ATUACAO } from "../../../models/SolicitacaoProfissional";
import { ehProfissional } from "../../../utils/papeis";
import { mensagemDeErro, respostaOk } from "../../../utils/resposta";
import { mapDispatchToProps, mapStateToProps } from "../../../utils/config";

const MIN_MENSAGEM = 10;
const MAX_MENSAGEM = 2000;

/**
 * Pedido de perfil Profissional.
 *
 * Não há pagamento nesta etapa do sistema: o usuário Comum preenche o
 * formulário, o pedido chega ao painel do administrador e é ele quem promove.
 * O backend (PapelValidation) é quem barra de fato o registro de áreas; este
 * modal só encaminha o usuário para o caminho certo em vez de um beco sem saída.
 *
 * Quatro estados, decididos por GET /solicitacoes-profissional/minha:
 *   formulario   nunca pediu (ou o último pedido foi aprovado e depois revertido)
 *   pendente     há um pedido em análise
 *   recusada     último pedido recusado — mostra o motivo e permite pedir de novo
 *   profissional o usuário já tem o papel
 */
function ModalSolicitarProfissional({ onClose, usuario, setUsuario }) {
  const [etapa, setEtapa] = useState('carregando');
  const [solicitacao, setSolicitacao] = useState(null);
  const [form, setForm] = useState({ ...SolicitacaoProfissional.model });
  const [errors, setErrors] = useState({});
  const [enviando, setEnviando] = useState(false);

  // O papel mudou no servidor (aprovado depois de o usuário já ter logado): a
  // sessão e o redux ainda guardam o antigo. Sem isto o usuário seguiria vendo
  // o modal mesmo já sendo Profissional.
  function atualizarPapelLocal(tipo) {
    try {
      const guardado = JSON.parse(sessionStorage.getItem('usuario'));
      if (guardado) sessionStorage.setItem('usuario', JSON.stringify({ ...guardado, tipo }));
    } catch (e) { /* sessionStorage indisponível: o redux já basta para a tela */ }
    if (usuario?.tipo !== tipo) setUsuario({ ...usuario, tipo });
  }

  async function carregar() {
    setEtapa('carregando');
    const resultado = await SolicitacaoProfissional.minha();

    if (!respostaOk(resultado)) {
      setErrors({ geral: mensagemDeErro(resultado) });
      setEtapa('erro');
      return;
    }

    const { tipo, solicitacao: ultima } = resultado.data;
    setSolicitacao(ultima);
    setErrors({});

    if (ehProfissional({ tipo })) {
      atualizarPapelLocal(tipo);
      setEtapa('profissional');
    } else if (ultima?.status === 'Pendente') {
      setEtapa('pendente');
    } else if (ultima?.status === 'Recusada') {
      setEtapa('recusada');
    } else {
      setEtapa('formulario');
    }
  }

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function alterar(campo, valor) {
    setForm((atual) => ({ ...atual, [campo]: valor }));
    setErrors((atual) => ({ ...atual, [campo]: '', geral: '' }));
  }

  function validar() {
    const novos = {};
    if (!form.tipo_atuacao) novos.tipo_atuacao = 'Escolha como você atua';
    if (form.mensagem.trim().length < MIN_MENSAGEM) {
      novos.mensagem = `Conte um pouco mais: mínimo de ${MIN_MENSAGEM} caracteres`;
    }
    if (form.area_estimada_ha !== '' && !(Number(form.area_estimada_ha) >= 0)) {
      novos.area_estimada_ha = 'Informe um número válido';
    }
    return novos;
  }

  async function enviar(e) {
    e.preventDefault();
    const novos = validar();
    setErrors(novos);
    if (Object.keys(novos).length) return;

    setEnviando(true);
    const resultado = await SolicitacaoProfissional.create({
      tipo_atuacao: form.tipo_atuacao,
      organizacao: form.organizacao.trim(),
      telefone: form.telefone.trim(),
      area_estimada_ha: form.area_estimada_ha === '' ? null : Number(form.area_estimada_ha),
      mensagem: form.mensagem.trim(),
    });

    if (respostaOk(resultado)) {
      setForm({ ...SolicitacaoProfissional.model });
      await carregar();
    } else {
      setErrors({ geral: mensagemDeErro(resultado) });
      // 409: já existe pedido pendente (ou o perfil já foi concedido) — a tela
      // estava desatualizada. Recarrega para mostrar o estado verdadeiro.
      if (resultado?.response?.status === 409) await carregar();
    }
    setEnviando(false);
  }

  return (
    <Overlay>
      <Card role="dialog" aria-modal="true" aria-labelledby="titulo-solicitar-profissional">
        <div className="top">
          <h3 id="titulo-solicitar-profissional">Perfil Profissional</h3>
          <span className="pro">PRO</span>
        </div>
        <button type="button" className="close" onClick={onClose} aria-label="Fechar">
          <img src={closeIcon} alt="" />
        </button>

        {etapa === 'carregando' &&
          <p className="carregando">Verificando o seu perfil...</p>
        }

        {etapa === 'erro' &&
          <>
            <p className="geral">{errors.geral}</p>
            <div className="acoes">
              <button type="button" onClick={carregar}>Tentar novamente</button>
              <button type="button" className="secundario" onClick={onClose}>Fechar</button>
            </div>
          </>
        }

        {etapa === 'profissional' &&
          <>
            <div className="estado">
              <strong>Você já tem o perfil Profissional</strong>
              Agora você pode registrar áreas de plantio (várias coordenadas e espécies) e criar subgrupos.
            </div>
            <div className="acoes">
              <button type="button" onClick={onClose}>Continuar</button>
            </div>
          </>
        }

        {etapa === 'pendente' &&
          <>
            <div className="estado">
              <strong>Seu pedido está em análise</strong>
              Enviado em {new Date(solicitacao?.createdAt).toLocaleDateString('pt-BR')}. A administração
              vai avaliar e você poderá registrar áreas e criar subgrupos assim que ele for aprovado.
            </div>
            <div className="acoes">
              <button type="button" onClick={onClose}>Entendi</button>
            </div>
          </>
        }

        {(etapa === 'recusada' || etapa === 'formulario') &&
          <>
            {etapa === 'recusada' &&
              <div className="estado recusada">
                <strong>Seu último pedido não foi aprovado</strong>
                {solicitacao?.resposta
                  ? <>Motivo informado: “{solicitacao.resposta}”.</>
                  : <>A administração não informou um motivo.</>}
                {' '}Você pode enviar um novo pedido com mais informações.
              </div>
            }

            <p className="intro">
              O registro de áreas e a criação de subgrupos são exclusivos do perfil Profissional. Peça o seu perfil: a
              administração analisa o pedido e libera o acesso.
            </p>
            <ul className="beneficios">
              <li>Áreas de plantio com várias coordenadas</li>
              <li>Subgrupos dentro dos grupos</li>
              <li>Várias espécies por área</li>
              <li>Registros ilimitados por área</li>
            </ul>

            <Formulario id="form-solicitar-profissional">
              <div className="form-group">
                <label htmlFor="tipo_atuacao">Como você atua?</label>
                <select id="tipo_atuacao" name="tipo_atuacao" value={form.tipo_atuacao}
                  onChange={(e) => alterar('tipo_atuacao', e.target.value)} className={errors.tipo_atuacao && 'input-error'}>
                  <option value="" disabled>Selecione</option>
                  {TIPOS_ATUACAO.map((tipo) => <option key={tipo} value={tipo}>{tipo}</option>)}
                </select>
                <span className="error">{errors.tipo_atuacao}</span>
              </div>

              <div className="form-group">
                <label htmlFor="organizacao">Organização ou empresa (opcional)</label>
                <input id="organizacao" name="organizacao" type="text" maxLength={150} value={form.organizacao}
                  placeholder="Nome da organização, empresa ou propriedade"
                  onChange={(e) => alterar('organizacao', e.target.value)} />
              </div>

              <div className="linha">
                <div className="form-group">
                  <label htmlFor="telefone">Telefone (opcional)</label>
                  <input id="telefone" name="telefone" type="tel" maxLength={20} value={form.telefone}
                    placeholder="(00) 00000-0000"
                    onChange={(e) => alterar('telefone', e.target.value)} />
                </div>
                <div className="form-group">
                  <label htmlFor="area_estimada_ha">Área em hectares (opcional)</label>
                  <input id="area_estimada_ha" name="area_estimada_ha" type="number" min="0" step="any" value={form.area_estimada_ha}
                    placeholder="Ex.: 40"
                    onChange={(e) => alterar('area_estimada_ha', e.target.value)} className={errors.area_estimada_ha && 'input-error'} />
                  <span className="error">{errors.area_estimada_ha}</span>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="mensagem">Por que você precisa do perfil Profissional?</label>
                <textarea id="mensagem" name="mensagem" maxLength={MAX_MENSAGEM} value={form.mensagem}
                  placeholder="Conte o que pretende plantar e onde."
                  onChange={(e) => alterar('mensagem', e.target.value)} className={errors.mensagem && 'input-error'} />
                <span className="contador">{form.mensagem.length}/{MAX_MENSAGEM}</span>
                <ContadorMinimo valor={form.mensagem} minimo={MIN_MENSAGEM} campo="A mensagem" />
              </div>

              <p className="geral">{errors.geral}</p>

              <div className="acoes">
                <button type="button" onClick={enviar} disabled={enviando}>
                  {enviando ? 'Enviando...' : 'Enviar pedido'}
                </button>
                <button type="button" className="secundario" onClick={onClose}>Agora não</button>
              </div>
            </Formulario>
          </>
        }
      </Card>
    </Overlay>
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(ModalSolicitarProfissional);
